import { useState, useEffect } from 'react';
import { api } from '@/api/client';

const typeLabels: Record<string, string> = {
  questionnaire: 'Анкета',
  interview: 'Интервью',
  rejection: 'Отказ',
};

const typeColors: Record<string, string> = {
  questionnaire: 'bg-accent-50 text-accent-700',
  interview: 'bg-primary-50 text-primary-700',
  rejection: 'bg-red-50 text-red-600',
};

const statusConfig: Record<string, { icon: string; color: string; label: string }> = {
  pending: { icon: 'ri-time-line', color: 'text-foreground-400', label: 'В очереди' },
  sent: { icon: 'ri-send-plane-line', color: 'text-foreground-500', label: 'Отправлено' },
  delivered: { icon: 'ri-check-double-line', color: 'text-emerald-600', label: 'Доставлено' },
  opened: { icon: 'ri-eye-line', color: 'text-primary-600', label: 'Открыто' },
  clicked: { icon: 'ri-cursor-line', color: 'text-accent-600', label: 'Клик' },
  bounced: { icon: 'ri-error-warning-line', color: 'text-red-600', label: 'Ошибка' },
};

interface Template { id: string; type: string; subject: string; body: string }
interface LogEntry { id: string; to_email: string; subject?: string; type?: string; status: string; sent_at?: string; created_at?: string }

export default function Emails() {
  const [activeTab, setActiveTab] = useState<'templates' | 'log'>('templates');
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [savedId, setSavedId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<Template[]>('/email-templates').catch(() => []),
      api.get<LogEntry[]>('/emails/log').catch(() => []),
    ]).then(([t, l]) => {
      setTemplates(t);
      setLogs(l);
    }).finally(() => setLoading(false));
  }, []);

  const updateTemplate = (id: string, field: 'subject' | 'body', value: string) => {
    setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
  };

  const toggleEdit = async (t: Template) => {
    if (editingTemplate === t.id) {
      // закрываем редактирование — сохраняем
      try {
        await api.put(`/email-templates/${t.id}`, { subject: t.subject, body: t.body });
        setSavedId(t.id);
        setTimeout(() => setSavedId(null), 2000);
      } catch (err) {
        alert((err as Error).message || 'Ошибка сохранения');
        return;
      }
      setEditingTemplate(null);
    } else {
      setEditingTemplate(t.id);
    }
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
          Письма и шаблоны
        </h1>
        <p className="text-sm text-foreground-500 mt-1">
          Шаблоны писем кандидатам и история отправок
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-background-200">
        <button
          onClick={() => setActiveTab('templates')}
          className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
            activeTab === 'templates'
              ? 'border-primary-500 text-primary-700'
              : 'border-transparent text-foreground-500 hover:text-foreground-700'
          }`}
        >
          <i className="ri-draft-line" />
          Шаблоны
          <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">
            {templates.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('log')}
          className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
            activeTab === 'log'
              ? 'border-primary-500 text-primary-700'
              : 'border-transparent text-foreground-500 hover:text-foreground-700'
          }`}
        >
          <i className="ri-history-line" />
          История отправок
          <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">
            {logs.length}
          </span>
        </button>
      </div>

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          {templates.length === 0 && (
            <div className="bg-white rounded-xl border border-background-200 p-8 text-center text-sm text-foreground-500">
              Шаблонов пока нет
            </div>
          )}
          {templates.map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-background-200 p-5">
              <div className="flex items-center gap-3 mb-4">
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${typeColors[t.type] || 'bg-background-100 text-foreground-500'}`}>
                  {typeLabels[t.type] || t.type}
                </span>
                {editingTemplate === t.id ? (
                  <input
                    value={t.subject}
                    onChange={(e) => updateTemplate(t.id, 'subject', e.target.value)}
                    className="flex-1 px-2 py-1 rounded bg-background-50 border border-background-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                ) : (
                  <span className="text-sm font-semibold text-foreground-900">{t.subject}</span>
                )}
                {savedId === t.id && (
                  <span className="text-xs text-emerald-600 font-medium"><i className="ri-check-line" /> Сохранено</span>
                )}
                <button
                  onClick={() => toggleEdit(t)}
                  className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors shrink-0 cursor-pointer ml-auto"
                >
                  <i className={editingTemplate === t.id ? 'ri-check-line' : 'ri-pencil-line'} />
                </button>
              </div>

              {editingTemplate === t.id ? (
                <textarea
                  value={t.body}
                  onChange={(e) => updateTemplate(t.id, 'body', e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y font-mono leading-relaxed"
                />
              ) : (
                <div className="bg-background-50 rounded-lg p-4 border border-background-200">
                  <pre className="text-sm text-foreground-700 whitespace-pre-wrap font-sans leading-relaxed">
                    {t.body}
                  </pre>
                </div>
              )}

              <div className="mt-3 flex items-center gap-2 text-xs text-foreground-400">
                <i className="ri-braces-line" />
                Переменные: {'{{name}}'}, {'{{vacancy_title}}'}, {'{{questionnaire_link}}'} и др.
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Log Tab */}
      {activeTab === 'log' && (
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          {logs.length === 0 && (
            <div className="p-8 text-center text-sm text-foreground-500">
              Писем пока не отправлялось (для отправки нужен подключённый Resend API-ключ)
            </div>
          )}
          {logs.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200 bg-background-50">
                    <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Получатель</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Тип письма</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">Статус</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500">Дата</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const cfg = statusConfig[log.status] || statusConfig.sent;
                    return (
                      <tr key={log.id} className="border-b border-background-100 last:border-0 hover:bg-background-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-primary-700 font-heading font-semibold text-xs">
                                {(log.to_email || '?').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium text-foreground-900">{log.to_email}</span>
                              {log.subject && <p className="text-xs text-foreground-400">{log.subject}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${typeColors[log.type || ''] || 'bg-background-100 text-foreground-500'}`}>
                            {typeLabels[log.type || ''] || log.type || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <i className={`${cfg.icon} ${cfg.color} text-sm`} />
                            <span className="text-xs text-foreground-600">{cfg.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-foreground-500">
                          {formatDate(log.sent_at || log.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
